import { z } from "zod";
import { AltAssetId, ClientId, DateSchema, ExternalAccountId, FartherAccountId, } from "../Types";
export const InvitationId = z.custom((val) => typeof val === "string" && z.string().uuid().safeParse(val).success);
export const serviceProfessionals = [
    "Accountant",
    "EstateLawyer",
    "FinancialAdvisor",
    "PropertyManager",
    "InsuranceBroker",
];
export const ServiceProfessional = z.enum(serviceProfessionals);
export const accessLevels = ["All", "Limited", "None"];
export const AccessLevel = z.enum(accessLevels);
export const invitationStatus = [
    "Pending",
    "Active",
    "Rejected",
    "Removed",
];
export const InvitationStatus = z.enum(invitationStatus);
export const resourceType = [
    "FartherAccount",
    "ExternalAccount",
    "ManualAccount",
];
export const ResourceType = z.enum(resourceType);
export const ManualAccountSharedResource = z.object({
    resourceId: AltAssetId,
    resourceType: ResourceType.extract(["ManualAccount"]),
});
export const ExternalAccountSharedResource = z.object({
    resourceId: ExternalAccountId,
    resourceType: ResourceType.extract(["ExternalAccount"]),
});
export const FartherAccountSharedResource = z.object({
    resourceId: FartherAccountId,
    resourceType: ResourceType.extract(["FartherAccount"]),
});
export const SharedResource = z.union([
    FartherAccountSharedResource,
    ExternalAccountSharedResource,
    ManualAccountSharedResource,
]);
export const Invitor = z.object({
    invitorId: ClientId,
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
});
export const Collaborator = z.object({
    collaboratorId: ClientId,
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
});
export const NewCollaborator = Collaborator.omit({ collaboratorId: true });
export const RelationTypeId = z.number().min(1).max(19); // there are only 19 relationship enums in Cockroach
export const PostBodyLimitedAccessSchema = z.object({
    invitorId: ClientId,
    collaborator: NewCollaborator,
    accessLevel: AccessLevel.extract(["Limited"]),
    relationTypeId: RelationTypeId.optional(),
    serviceProfessionalType: ServiceProfessional.optional(),
    resources: SharedResource.array().min(1),
    createAuthorization: z.boolean().optional(),
    notify: z.boolean().optional(),
});
export const PostBodyAllOrNoneAccessSchema = z.object({
    invitorId: ClientId,
    collaborator: NewCollaborator,
    accessLevel: AccessLevel.exclude(["Limited"]),
    relationTypeId: RelationTypeId.optional(),
    resources: SharedResource.array().min(0).max(0),
    createAuthorization: z.boolean().optional(),
    notify: z.boolean().optional(),
    serviceProfessionalType: ServiceProfessional.optional(),
});
export const PostBody = z.union([
    PostBodyLimitedAccessSchema,
    PostBodyAllOrNoneAccessSchema,
]);
export const PostResponse = z.object({
    invitationId: InvitationId,
});
export const DeleteResponse = z.object({
    message: z.string(),
});
export const BaseInvitation = z.object({
    invitationId: InvitationId,
    accessLevel: AccessLevel,
    status: InvitationStatus,
    createdAt: DateSchema,
    updatedAt: DateSchema,
    resources: SharedResource.array(),
});
export const SentInvitation = BaseInvitation.extend({
    invitorId: ClientId,
    collaborator: Collaborator,
    notify: z.boolean(),
    relationTypeId: RelationTypeId.optional(),
    serviceProfessionalType: ServiceProfessional.optional(),
});
export const ReceivedInvitation = BaseInvitation.extend({
    collaboratorId: ClientId,
    invitor: Invitor,
});
export const GetResponse = z.object({
    sent: SentInvitation.array(),
    received: ReceivedInvitation.array(),
});
export const AllOrNoneAccessBody = z.object({
    accessLevel: AccessLevel.extract(["All", "None"]),
    resources: SharedResource.array().min(0).max(0),
    notify: z.boolean(),
});
export const LimitedAccessBody = z.object({
    accessLevel: AccessLevel.extract(["Limited"]),
    resources: SharedResource.array().min(1),
    notify: z.boolean(),
});
export const PutBody = z.union([AllOrNoneAccessBody, LimitedAccessBody]);
export const PutResponse = z.object({
    accessLevel: AccessLevel,
});
export const PatchStatusBody = z.object({
    status: InvitationStatus.exclude(["Pending"]),
    relationTypeId: RelationTypeId.optional(),
    serviceProfessionalType: ServiceProfessional.optional(),
});
export const PatchRelationBody = z.object({
    status: InvitationStatus.exclude(["Pending"]).optional(),
    relationTypeId: RelationTypeId,
    serviceProfessionalType: ServiceProfessional.optional(),
});
export const PatchServiceProBody = z.object({
    status: InvitationStatus.exclude(["Pending"]).optional(),
    relationTypeId: RelationTypeId.optional(),
    serviceProfessionalType: ServiceProfessional,
});
export const PatchBody = z.union([
    PatchStatusBody,
    PatchRelationBody,
    PatchServiceProBody,
]);
export const PatchResponse = z.object({
    status: InvitationStatus,
    relationTypeId: RelationTypeId.optional(),
    professionType: ServiceProfessional.optional(),
});
//# sourceMappingURL=Types.js.map